import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { CoreService, InMemoryCacheService, MessageType, UtilsService } from 'src/app/common';
import { environment } from 'src/environments/environment';
import { SeoApiService } from '../seo-api.service';
import { webMentions } from '../models/brand-monitoring-requests';
import { BacklinkResearchResponse, CompetitorResearchResponse, DomainResearchResult, KeywordResearchResponse, ResearchRecord } from './domain-research.models';

@Injectable({
  providedIn: 'root'
})
export class DomainResearchService {

    private readonly responseCache: Map<string, ResearchRecord> = new Map<string, ResearchRecord>();
    private readonly inFlightRequests: Map<string, Promise<ResearchRecord>> = new Map<string, Promise<ResearchRecord>>();

    private core = inject(CoreService<{}>);
    private utils = inject(UtilsService);
    private http = inject(HttpClient);
    private cache = inject(InMemoryCacheService);
    private seoApiService = inject(SeoApiService);

    private selectedKeyword: Array<{ keyword?: string }> = [];
    public totalMentions: number = 0;
    public selectedSentiment: { name?: string } | null = null;
    public datas: unknown[] = [];

    public async getKeywordResearch(projectId: string): Promise<DomainResearchResult<KeywordResearchResponse>> {
        return this.loadSections<KeywordResearchResponse>(projectId, {
            rankedKeywords: `keywordRecommendation/rankedKeywords/project/${projectId}`,
            paidAds: `keywordRecommendation/paidAds/project/${projectId}`
        });
    }

    public async getKeywordOverview(projectId: string): Promise<DomainResearchResult<KeywordResearchResponse>> {
        return this.loadSections<KeywordResearchResponse>(projectId, {
            rankedKeywords: `keywordRecommendation/rankedKeywords/project/${projectId}`
        });
    }

    public async getCompetitorResearch(projectId: string): Promise<DomainResearchResult<CompetitorResearchResponse>> {
        return this.loadSections<CompetitorResearchResponse>(projectId, {
            keywordIntersect: `keywordRecommendation/keywordIntersect/project/${projectId}`,
            competitorRankedKeywords: `keywordRecommendation/competitorRankedKeywords/project/${projectId}`,
            paidAds: `keywordRecommendation/paidAds/project/${projectId}`
        });
    }

    public async getBacklinkResearch(projectId: string): Promise<DomainResearchResult<BacklinkResearchResponse>> {
        return this.loadSections<BacklinkResearchResponse>(projectId, {
            topBacklinks: `backlinkRecommendation/topBacklinks/project/${projectId}`,
            topPages: `backlinkRecommendation/topPages/project/${projectId}`
        });
    }

    public async getBacklinkOverview(projectId: string): Promise<DomainResearchResult<BacklinkResearchResponse>> {
        return this.loadSections<BacklinkResearchResponse>(projectId, {
            topBacklinks: `backlinkRecommendation/topBacklinks/project/${projectId}`
        });
    }

    public async getDomainResearchData(projectId: string, type: 'keyword' | 'backlink'): Promise<DomainResearchResult<KeywordResearchResponse | BacklinkResearchResponse>> {
        if (type === 'keyword') {
            const keywordResult = await this.getKeywordResearch(projectId);
            const competitorResult = await this.getCompetitorResearch(projectId);
            return {
                data: { ...keywordResult.data, keywordIntersect: competitorResult.data?.keywordIntersect || null },
                error: keywordResult.error || competitorResult.error,
                sectionErrors: { ...keywordResult.sectionErrors, ...competitorResult.sectionErrors }
            };
        }
        return this.loadSections<BacklinkResearchResponse>(projectId, {
            topBacklinks: `backlinkRecommendation/topBacklinks/project/${projectId}`,
            topPages: `backlinkRecommendation/topPages/project/${projectId}`,
            backlinkCompetitors: `backlinkRecommendation/backlinkCompetitors/project/${projectId}`,
            backlinkCompetitorIntersect: `backlinkRecommendation/backlinkCompetitorIntersect/project/${projectId}`
        });
    }

    public clearProjectCache(projectId: string): void {
        for (const key of this.responseCache.keys()) {
            if (key.startsWith(`${projectId}:`)) this.responseCache.delete(key);
        }
    }

    private async loadSections<T extends object>(projectId: string, endpoints: Partial<Record<keyof T, string>>): Promise<DomainResearchResult<T>> {
        const entries = Object.entries(endpoints) as Array<[keyof T, string]>;
        const settled = await Promise.all(entries.map(([, endpoint]) => this.requestSnapshot(projectId, endpoint)
            .then(value => ({ fulfilled: true as const, value }))
            .catch(reason => ({ fulfilled: false as const, reason }))));
        const data = {} as T;
        const sectionErrors: Partial<Record<keyof T, unknown>> = {};
        settled.forEach((result, index) => {
            const key = entries[index][0];
            if ('value' in result) data[key] = result.value as T[keyof T];
            else { data[key] = null as T[keyof T]; sectionErrors[key] = result.reason; }
        });
        const failedCount: number = Object.keys(sectionErrors).length;
        return { data, error: failedCount === entries.length ? Object.values(sectionErrors)[0] : null, sectionErrors };
    }

    private requestSnapshot(projectId: string, endpoint: string): Promise<ResearchRecord> {
        const key: string = `${projectId}:${endpoint}`;
        const cached = this.responseCache.get(key);
        if (cached) return Promise.resolve(cached);
        const existing = this.inFlightRequests.get(key);
        if (existing) return existing;
        const request = firstValueFrom(this.core.get(`${environment.seoUrl}/${endpoint}`))
            .then((value: unknown) => {
                const response = this.normalizeSnapshot((value || {}) as ResearchRecord);
                this.responseCache.set(key, response);
                return response;
            })
            .finally(() => this.inFlightRequests.delete(key));
        this.inFlightRequests.set(key, request);
        return request;
    }

    private normalizeSnapshot(response: ResearchRecord): ResearchRecord {
        const normalizedResponse: ResearchRecord = response['backlinks'] && !response['topBacklinks']
            ? { ...response, topBacklinks: response['backlinks'] }
            : response;
        const intersections = normalizedResponse['competitorIntersect'];
        if (!Array.isArray(intersections)) return normalizedResponse;
        return {
            ...normalizedResponse,
            competitorIntersect: intersections.map((item: unknown) => {
                if (!item || typeof item !== 'object') return item;
                const record = item as ResearchRecord;
                const legacy = record['domainInersection'];
                if (!legacy || record['domainIntersection']) return record;
                const { domainInersection: _removed, ...normalized } = record;
                return { ...normalized, domainIntersection: legacy };
            })
        };
    }

    public async getGMBAudit() {

        let keywordPayload = "";

        // Check if BOTH cid AND placeId are empty or null
        const cidInvalid = !this.cache?.selectedProject?.googleMyBusiness?.cid || this.cache.selectedProject.googleMyBusiness.cid === '';

        const placeIdInvalid = !this.cache?.selectedProject?.googleMyBusiness?.placeId || this.cache.selectedProject.googleMyBusiness.placeId === '';

        // Only return null if BOTH are invalid
        if (cidInvalid && placeIdInvalid) {
        this.utils.logger.info("returning null - both cid and placeId are missing");
        return null;
        }

        // Choose which ID to use based on availability
        if (cidInvalid) {
        keywordPayload = "place_id:" + this.cache.selectedProject.googleMyBusiness.placeId;
        } else {
        keywordPayload = "cid:" + this.cache.selectedProject.googleMyBusiness.cid;
        }

        const payload = {
            "languageCode": this.cache?.selectedProject?.googleMyBusiness?.languageCode,
            "locationCode": this.cache?.selectedProject?.googleMyBusiness?.locationCode,
            "keyword": keywordPayload,
            "userId": this.cache.user.id
        }

        try {
            const { data, error } = await this.seoApiService.getGMBAudit(payload);

            if (error) {
                throw error;
            }

            return data;
        } catch (error) {
            return null;
        }

    }

    public async getSearch() {
        this.cache.loading = true;

        const cat = await this.getCategories();

        this.selectedKeyword = this.cache?.selectedProject?.brandMonitoring?.brandKeywords;

        // Check if selectedKeyword array exists and has a valid keyword
        if (!this.selectedKeyword || !this.selectedKeyword.length || !this.selectedKeyword[0]?.keyword) {
            this.cache.loading = false;
            return null;
        }

        const payload: webMentions = {
            keyword: this.selectedKeyword[0]?.keyword,
            filtersString: '',
            internalListLimit: this.cache?.seoLimits?.contentAnalysisSsearchInternal,
            limit: this.cache?.seoLimits?.contentAnalysisSearch
        }


        const { data, error } = await this.seoApiService.webMentionOverview(payload);

        if (error) {
            // this.utils.errorMessage(MessageType.TOAST, 'Error', error);
            this.utils.handleApiError(error, 'Failed to fetch web mentions.');
            this.cache.loading = false;
            return false;
        }

        this.totalMentions = data.itemsCount;

        data.items.forEach((q) => {
            q.rating = q.ratings.map(r => r.ratingValue)[0] ?? 0,
            q.sentiment = this.getHighestConnotations(q.contentInfo.connotationTypes, this.selectedSentiment?.name);
            q.sentimentConnotation = this.getHighestSentimentConnotations(q.contentInfo.sentimentConnotations);
            q.category = cat.find(c => c.categoryCode === q.contentInfo.textCategory[0])?.categoryName
            q.pageCategories = q?.pageCategory.map(categoryCode => {
                return cat.find(c => c.categoryCode === categoryCode)?.categoryName || 'Unknown';
            });
        });

        this.datas = data.items;

        this.cache.loading = false;
        return {
            totalMentions: this.totalMentions,
            data: this.datas
        };
    }
    private async getCategories() {

        const {data, error} = await this.seoApiService.getCategories();

        if(error) {
            this.utils.errorMessage(MessageType.TOAST, 'Error', error);
        }

        return data ;
    }

    getHighestConnotations(connotationsTypes: {positive: number; negative: number, neutral: number}, filterOption?: string) : string {

        if(!connotationsTypes) return 'N/A';
        let {positive, negative, neutral } = connotationsTypes;

        if(!filterOption) {
            const highestValue = Math.max(positive, negative, neutral);
            if(highestValue === positive) {
               return 'Positive';
            }
            if(highestValue === negative) {
               return 'Negative';
            }
            if(highestValue === neutral) {
               return 'Neutral';
            };
        }
        else {
            return filterOption;
        }

        return 'No data available';
    }
    getHighestSentimentConnotations(connotationsTypes: {anger: number; fun: number; happiness: number; love: number; sadness: number; share: number}) : string {

        if(!connotationsTypes) return 'N/A';

        const {anger, fun, happiness, love, sadness, share } = connotationsTypes;
        const highestValue = Math.max(anger, fun, happiness, love, sadness, share);

        if(highestValue === anger) return 'Anger';
        if(highestValue === fun) return 'Fun';
        if(highestValue === happiness) return 'Happiness';
        if(highestValue === love) return 'Love';
        if(highestValue === sadness) return 'Sadness';
        if(highestValue === share) return 'Share';

        return 'No data available';
}
}
